import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { EmptyState, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { liveBoard, type SeatResult } from './api'
import { CountedStatuses, ReturnStatusChip, seatLabel } from './vocab'

/**
 * Every race at once, as the count comes in.
 *
 * ## The completeness number is the headline, not the votes
 *
 * "Achieng 4.2m" means nothing on its own — it is a fact about how much has been counted,
 * not about who is winning. So every card leads with **how many units have reported out of
 * how many should**, and the votes sit under it. A board that shows totals without that
 * denominator invites a reader to treat a tenth of a count as a result.
 *
 * ## And it says what it counted
 *
 * Only VERIFIED and CERTIFIED returns feed these figures. The line saying so is not
 * decoration: the same screen with unverified returns included would show different numbers,
 * and a reader who cannot tell which they are looking at cannot use either.
 */
export function LiveBoardPage() {
  const { electionId } = useElectionContext()
  const [rows, setRows] = useState<SeatResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  // `electionId` IS in the dependency array — see the module's own trap.
  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(await liveBoard({ electionId }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A results board" />
  if (rows === null) return <LoadingBlock label="Counting…" />

  return (
    <div className="space-y-4">
      <ErrorStrip message={error} className="rounded-md border" />

      {rows.length === 0 && !error && (
        <EmptyState
          title="Nothing has been filed yet"
          hint="Results appear here as returns are entered and verified. A race with no
                returns is left out rather than shown as a row of zeros — on a board, '0 of 0'
                and '0 of 1,450' mean opposite things." />
      )}

      {rows.map((seat) => <SeatCard key={seat.seatTypeCode} seat={seat} />)}
    </div>
  )
}

function SeatCard({ seat }: { seat: SeatResult }) {
  const leader = seat.candidates[0]
  const reportingPct = seat.unitsExpected && seat.unitsExpected > 0
    ? (seat.unitsReporting / seat.unitsExpected) * 100
    : null

  return (
    <section className="erp-card p-3 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-erp-base font-semibold">{seatLabel(seat.seatTypeCode)}</h3>
          {/* The completeness number leads. It is the fact that qualifies every other one. */}
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            <strong className="text-foreground tabular-nums">
              {fmtNumber(seat.unitsReporting)}
              {seat.unitsExpected != null && <> of {fmtNumber(seat.unitsExpected)}</>}
            </strong>{' '}
            {seat.unitsExpected != null ? 'units reporting' : 'units reporting (total unknown)'}
            {reportingPct != null && <> · {reportingPct.toFixed(1)}%</>}
            {seat.turnoutPct != null && <> · turnout {seat.turnoutPct}%</>}
          </p>
        </div>
        <Link to={`/tallying/seats?seat=${seat.seatTypeCode}`}
          className="text-erp-xs text-chocolate hover:underline">
          Open the race →
        </Link>
      </div>

      {reportingPct != null && (
        <div className="h-1 w-full rounded-full bg-muted overflow-hidden" aria-hidden>
          <div className="h-full bg-chocolate transition-[width]"
            style={{ width: `${Math.min(100, reportingPct)}%` }} />
        </div>
      )}

      {seat.candidates.length === 0 ? (
        /*
           "None counted" and "none filed" are different facts, and showing the first when
           the second is false is what makes a working board look broken. If returns exist
           but are not yet verified, say so and point at the queue — the figures are not
           missing, they are waiting for a second pair of eyes.
        */
        <p className="text-erp-xs text-muted-foreground">
          {uncounted(seat) > 0
            ? `No figures counted yet — ${uncounted(seat)} return${uncounted(seat) === 1 ? ' is' : 's are'} filed but not verified. Boards count verified and certified returns only.`
            : 'No candidate figures counted yet.'}
        </p>
      ) : (
        <ol className="space-y-1.5">
          {seat.candidates.slice(0, 6).map((c) => {
            const share = c.sharePct ?? 0
            return (
              <li key={c.candidacyId} className="space-y-0.5">
                <div className="flex items-baseline justify-between gap-2 text-erp-sm">
                  <span className="truncate">
                    {c.ballotName}
                    {c.independent && (
                      <span className="ml-1.5 text-erp-xs text-muted-foreground">Independent</span>
                    )}
                  </span>
                  <span className="tabular-nums shrink-0">
                    {fmtNumber(c.votes)}
                    <span className="ml-1.5 text-erp-xs text-muted-foreground">
                      {c.sharePct == null ? '' : `${c.sharePct}%`}
                    </span>
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden" aria-hidden>
                  <div className={cn('h-full rounded-full',
                    c.candidacyId === leader?.candidacyId ? 'bg-chocolate' : 'bg-muted-foreground/40')}
                    style={{ width: `${Math.min(100, share)}%` }} />
                </div>
              </li>
            )
          })}
        </ol>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-2">
        <CountedStatuses statuses={seat.countedStatuses} />
        {/* What the figure does NOT include. This is the honest half of the card. */}
        {seat.statusBreakdown
          .filter((s) => !seat.countedStatuses.includes(s.statusCode))
          .map((s) => (
            <span key={s.statusCode} className="inline-flex items-center gap-1 text-erp-xs">
              <ReturnStatusChip code={s.statusCode} />
              <span className="tabular-nums text-muted-foreground">{fmtNumber(s.returns)}</span>
            </span>
          ))}
      </div>
    </section>
  )
}

/** Returns that exist for this seat but are excluded by the counted-status filter. */
function uncounted(seat: { countedStatuses: string[]; statusBreakdown: { statusCode: string; returns: number }[] }) {
  return seat.statusBreakdown
    .filter((s) => !seat.countedStatuses.includes(s.statusCode))
    .reduce((n, s) => n + s.returns, 0)
}
